import React, { useState, useRef, useEffect } from 'react';
import {
  Box,
  Container,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  Alert,
  Grid,
  IconButton,
  LinearProgress
} from '@mui/material';
import {
  CloudUpload as UploadIcon,
  Close as CloseIcon,
  CheckCircle as CheckIcon
} from '@mui/icons-material';
import PhoneInput from '../components/PhoneInput';
import { Headset } from 'lucide-react';

export default function Support() {
  const [form, setForm] = useState({
    name: '',
    email: '',
    phone: '',
    phone_country_code: '+1',
    description: ''
  });
  
  const [screenshots, setScreenshots] = useState([]);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [submittedEmail, setSubmittedEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const intervalIdRef = useRef(null);

  useEffect(() => {
    return () => {
      if (intervalIdRef.current) {
        clearInterval(intervalIdRef.current);
      }
    };
  }, []);

  const EMAIL_REGEX = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
    setError('');
  };

  const handlePhoneChange = (value) => {
    setForm({ ...form, phone: value });
  };

  const handleCountryCodeChange = (value) => {
    setForm({ ...form, phone_country_code: value });
  };

  const validatePhone = (phone) => {
    const digitsOnly = phone.replace(/\D/g, '');
    return digitsOnly.length >= 5;
  };

  const validateName = (name) => {
    return name.trim().length >= 2 && name.trim().length <= 100;
  };

  const handleFileChange = (e) => {
    const files = Array.from(e.target.files);
    const validFiles = [];
    const maxSize = 5 * 1024 * 1024; // 5MB
    const maxFiles = 5;

    if (screenshots.length + files.length > maxFiles) {
      setError(`You can only upload up to ${maxFiles} screenshots`);
      return;
    }

    for (const file of files) {
      if (file.size > maxSize) {
        setError(`File ${file.name} is too large. Maximum size is 5MB`);
        e.target.value = '';
        return;
      }

      if (!file.type.startsWith('image/')) {
        setError(`File ${file.name} is not an image`);
        e.target.value = '';
        return;
      }
    }

    files.forEach(file => {
      const reader = new FileReader();
      reader.onloadend = () => {
        validFiles.push({
          file,
          preview: reader.result,
          name: file.name,
          size: file.size
        });

        if (validFiles.length === files.length) {
          setScreenshots([...screenshots, ...validFiles]);
          setError('');
        }
      };
      reader.readAsDataURL(file);
    });

    // Reset input
    e.target.value = '';
  };

  const removeScreenshot = (index) => {
    setScreenshots(screenshots.filter((_, i) => i !== index));
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess(false);

    // Validation
    if (!form.name.trim()) {
      setError('Please enter your name');
      return;
    }

    if (!validateName(form.name)) {
      setError('Name must be between 2 and 100 characters');
      return;
    }

    if (!form.email.trim()) {
      setError('Please enter your email address');
      return;
    }

    if (!EMAIL_REGEX.test(form.email)) {
      setError('Please enter a valid email address');
      return;
    }

    if (form.phone && !validatePhone(form.phone)) {
      setError('Please enter a valid phone number (at least 5 digits)');
      return;
    }

    if (!form.description.trim()) {
      setError('Please describe your issue');
      return;
    }

    if (form.description.trim().length < 10) {
      setError('Please provide more details (at least 10 characters)');
      return;
    }

    setLoading(true);
    setUploadProgress(0);

    try {
      // Create FormData for file upload
      const formData = new FormData();
      formData.append('name', form.name);
      formData.append('email', form.email);
      formData.append('phone', form.phone);
      formData.append('phone_country_code', form.phone_country_code);
      formData.append('description', form.description);

      // Append all screenshots
      screenshots.forEach((screenshot, index) => {
        formData.append('screenshots', screenshot.file);
      });

      // Simulate upload progress
      intervalIdRef.current = setInterval(() => {
        setUploadProgress(prev => {
          if (prev >= 90) {
            if (intervalIdRef.current) {
              clearInterval(intervalIdRef.current);
              intervalIdRef.current = null;
            }
            return 90;
          }
          return prev + 10;
        });
      }, 100);

      // TODO: Replace with actual API endpoint when ready
      // const response = await axios.post(`${API_BASE}/api/support/ticket`, formData, {
      //   headers: {
      //     'Content-Type': 'multipart/form-data',
      //   },
      // });

      // Simulate API call
      await new Promise((resolve) => setTimeout(resolve, 2000));
      
      setUploadProgress(100);

      setSubmittedEmail(form.email);
      setSuccess(true);
      setForm({
        name: '',
        email: '',
        phone: '',
        phone_country_code: '+1',
        description: ''
      });
      setScreenshots([]);
      setUploadProgress(0);

      // Scroll to top to show success message
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (err) {
      setError('Failed to submit support request. Please try again or contact us at support@sniffr.ai');
      console.error('Support submission error:', err);
    } finally {
      if (intervalIdRef.current) {
        clearInterval(intervalIdRef.current);
        intervalIdRef.current = null;
      }
      setLoading(false);
    }
  };

  return (
    <Box sx={{ bgcolor: '#f8fafc', minHeight: '100vh', py: 6 }}>
      <Container maxWidth="md">
        {/* Header */}
        <Box sx={{ mb: 4, textAlign: 'center' }}>
          <Box sx={{ 
            display: 'inline-flex', 
            alignItems: 'center', 
            justifyContent: 'center',
            width: 64,
            height: 64,
            mb: 2
          }}>
            <Headset size={32} color="#000" />
          </Box>
          <Typography variant="h3" sx={{ fontWeight: 700, mb: 2, color: '#111827' }}>
            Get Support
          </Typography>
          <Typography variant="body1" sx={{ color: '#6b7280', fontSize: '16px', maxWidth: 700, mx: 'auto' }}>
            Having trouble? Submit a support ticket and our team will get back to you as soon as possible.
          </Typography>
        </Box>

        {/* Success Message */}
        {success && (
          <Alert 
            severity="success" 
            icon={<CheckIcon />}
            sx={{ mb: 3, borderRadius: 2, fontSize: 15 }}
          >
            <strong>Support ticket submitted successfully!</strong> We've received your request and will respond within 24 hours. 
            You'll receive a confirmation email at <strong>{submittedEmail}</strong>
          </Alert>
        )}

        {/* Support Form */}
        <Card sx={{ 
          borderRadius: 2,
          boxShadow: '0 1px 3px 0 rgba(0,0,0,0.1)',
          border: '1px solid #e2e8f0'
        }}>
          <CardContent sx={{ p: 4 }}>
            {error && <Alert severity="error" sx={{ mb: 3, borderRadius: 2 }}>{error}</Alert>}
            
            <form onSubmit={handleSubmit}>
              <Grid container spacing={3}>
                {/* Name Field */}
                <Grid item xs={12}>
                  <TextField
                    label="Name"
                    name="name"
                    value={form.name}
                    onChange={handleChange}
                    fullWidth
                    required
                    placeholder="John Doe"
                    disabled={loading}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        '& fieldset': { 
                          borderColor: form.name ? 
                            (validateName(form.name) ? '#4caf50' : '#f44336') : '#e0e0e0'
                        },
                        '&:hover fieldset': { borderColor: '#bdbdbd' },
                        '&.Mui-focused fieldset': { borderColor: '#000' },
                        borderRadius: 1,
                        bgcolor: '#fff'
                      }
                    }}
                  />
                </Grid>

                {/* Email Field */}
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="Email"
                    name="email"
                    type="email"
                    value={form.email}
                    onChange={handleChange}
                    fullWidth
                    required
                    placeholder="you@company.com"
                    disabled={loading}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        '& fieldset': { 
                          borderColor: form.email ? 
                            (EMAIL_REGEX.test(form.email) ? '#4caf50' : '#f44336') : '#e0e0e0'
                        },
                        '&:hover fieldset': { borderColor: '#bdbdbd' },
                        '&.Mui-focused fieldset': { borderColor: '#000' },
                        borderRadius: 1,
                        bgcolor: '#fff'
                      }
                    }}
                  />
                </Grid>

                {/* Phone Field */}
                <Grid item xs={12} sm={6}>
                  <PhoneInput
                    value={form.phone}
                    countryCode={form.phone_country_code}
                    onChange={handlePhoneChange}
                    onCountryCodeChange={handleCountryCodeChange}
                    label="Phone Number (Optional)"
                    disabled={loading}
                    error={form.phone && !validatePhone(form.phone)}
                    helperText={form.phone && !validatePhone(form.phone) ? 'Please enter at least 5 digits' : ''}
                  />
                </Grid>

                {/* Description Field */}
                <Grid item xs={12}>
                  <TextField
                    label="Description"
                    name="description"
                    value={form.description}
                    onChange={handleChange}
                    fullWidth
                    required
                    multiline
                    rows={6}
                    placeholder="Please describe your issue in detail..."
                    disabled={loading}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        '& fieldset': { borderColor: '#e0e0e0' },
                        '&:hover fieldset': { borderColor: '#bdbdbd' },
                        '&.Mui-focused fieldset': { borderColor: '#000' },
                        borderRadius: 1,
                        bgcolor: '#fff'
                      }
                    }}
                  />
                  <Typography variant="caption" sx={{ color: '#6b7280', mt: 0.5, display: 'block' }}>
                    {form.description.length} characters (minimum 10 required)
                  </Typography>
                </Grid>

                {/* Screenshot Upload */}
                <Grid item xs={12}>
                  <Box sx={{ 
                    border: '2px dashed #e0e0e0',
                    borderRadius: 2,
                    p: 3,
                    textAlign: 'center',
                    bgcolor: '#f9fafb',
                    transition: 'all 0.3s',
                    '&:hover': {
                      borderColor: '#bdbdbd',
                      bgcolor: '#f3f4f6'
                    }
                  }}>
                    <input
                      accept="image/*"
                      style={{ display: 'none' }}
                      id="screenshot-upload"
                      multiple
                      type="file"
                      onChange={handleFileChange}
                      disabled={loading || screenshots.length >= 5}
                    />
                    <label htmlFor="screenshot-upload">
                      <IconButton 
                        component="span" 
                        disabled={loading || screenshots.length >= 5}
                        sx={{ 
                          bgcolor: '#000',
                          color: '#fff',
                          mb: 1,
                          '&:hover': { bgcolor: '#333' },
                          '&:disabled': { bgcolor: '#e0e0e0', color: '#9e9e9e' }
                        }}
                      >
                        <UploadIcon />
                      </IconButton>
                    </label>
                    <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5, color: '#374151' }}>
                      Upload Screenshots
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#6b7280', mb: 1 }}>
                      Click to upload images (PNG, JPG, GIF)
                    </Typography>
                    <Typography variant="caption" sx={{ color: '#9ca3af' }}>
                      Maximum 5 files, 5MB each
                    </Typography>
                  </Box>

                  {/* Upload Progress */}
                  {loading && uploadProgress > 0 && (
                    <Box sx={{ mt: 2 }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                        <Typography variant="body2" sx={{ color: '#6b7280' }}>
                          Uploading...
                        </Typography>
                        <Typography variant="body2" sx={{ color: '#6b7280' }}>
                          {uploadProgress}%
                        </Typography>
                      </Box>
                      <LinearProgress 
                        variant="determinate" 
                        value={uploadProgress} 
                        sx={{ 
                          height: 8, 
                          borderRadius: 1,
                          bgcolor: '#e0e0e0',
                          '& .MuiLinearProgress-bar': {
                            bgcolor: '#000'
                          }
                        }}
                      />
                    </Box>
                  )}

                  {/* Screenshot Previews */}
                  {screenshots.length > 0 && (
                    <Box sx={{ mt: 2 }}>
                      <Typography variant="body2" sx={{ fontWeight: 600, mb: 1, color: '#374151' }}>
                        Attached Files ({screenshots.length}/5)
                      </Typography>
                      <Grid container spacing={2}>
                        {screenshots.map((screenshot, index) => (
                          <Grid item xs={12} sm={6} key={index}>
                            <Box sx={{ 
                              position: 'relative',
                              border: '1px solid #e0e0e0',
                              borderRadius: 1,
                              p: 1,
                              display: 'flex',
                              alignItems: 'center',
                              gap: 2,
                              bgcolor: '#fff'
                            }}>
                              <Box
                                component="img"
                                src={screenshot.preview}
                                alt={screenshot.name}
                                sx={{
                                  width: 60,
                                  height: 60,
                                  objectFit: 'cover',
                                  borderRadius: 1,
                                  border: '1px solid #e0e0e0'
                                }}
                              />
                              <Box sx={{ flex: 1, minWidth: 0 }}>
                                <Typography 
                                  variant="body2" 
                                  sx={{ 
                                    fontWeight: 500, 
                                    color: '#374151',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap'
                                  }}
                                >
                                  {screenshot.name}
                                </Typography>
                                <Typography variant="caption" sx={{ color: '#6b7280' }}>
                                  {formatFileSize(screenshot.size)}
                                </Typography>
                              </Box>
                              <IconButton
                                size="small"
                                onClick={() => removeScreenshot(index)}
                                disabled={loading}
                                sx={{ 
                                  color: '#ef4444',
                                  '&:hover': { bgcolor: '#fee2e2' }
                                }}
                              >
                                <CloseIcon fontSize="small" />
                              </IconButton>
                            </Box>
                          </Grid>
                        ))}
                      </Grid>
                    </Box>
                  )}
                </Grid>

                {/* Submit Button */}
                <Grid item xs={12}>
                  <Button
                    type="submit"
                    variant="contained"
                    fullWidth
                    size="large"
                    disabled={loading}
                    sx={{ 
                      mt: 2,
                      py: 1.5,
                      fontWeight: 600,
                      borderRadius: 1,
                      bgcolor: '#000',
                      color: '#fff',
                      textTransform: 'none',
                      fontSize: 16,
                      '&:hover': { bgcolor: '#333' },
                      '&:disabled': { bgcolor: '#e0e0e0', color: '#9e9e9e' }
                    }}
                  >
                    {loading ? 'Submitting...' : 'Submit Support Ticket'}
                  </Button>
                </Grid>
              </Grid>
            </form>
          </CardContent>
        </Card>

        {/* Help Text */}
        <Box sx={{ mt: 4, textAlign: 'center' }}>
          <Typography variant="body2" sx={{ color: '#6b7280', mb: 1 }}>
            Need immediate assistance?
          </Typography>
          <Typography variant="body2" sx={{ color: '#374151' }}>
            Email us directly at{' '}
            <a 
              href="mailto:support@sniffr.ai" 
              style={{ 
                color: '#000', 
                fontWeight: 600,
                textDecoration: 'none'
              }}
            >
              support@sniffr.ai
            </a>
          </Typography>
        </Box>
      </Container>
    </Box>
  );
}